import { getPrefixedLogger } from '@ringcentral/rwc-utils/src/rwc-logger';
import {
    isValidToken,
    safeParse,
    isGuestToken,
    isExpiredToken,
    isValidInitialDiscovery,
    isValidExternalDiscovery,
} from '@low/validate-token';

const logger = getPrefixedLogger('in-memory-sdk-storage');

const PLATFORM = 'rc-platform';
const DISCOVERY_EXTERNAL = 'rc-platform-discovery-external';
const DISCOVERY_INITIAL = 'rc-platform-discovery-initial';
const specialKeys = [PLATFORM, DISCOVERY_EXTERNAL, DISCOVERY_INITIAL];

const counter = (
    (c = 0) =>
    () =>
        c++
)();

export default class InMemorySdkStorage implements Storage {
    #instanceId = 'id' + counter();

    #data = new Map<string, string>();

    get length() {
        logger.debug(`${this.#instanceId}: get length of ${this.#data.size}`);

        return this.#data.size;
    }

    clear(): void {
        logger.info(`${this.#instanceId}: ${new Error('clear sdk storage').stack}`);

        this.#data.clear();
    }

    getItem(key: string): string | null {
        const result = this.#data.get(key) ?? null;
        logger.debug(`${this.#instanceId}: get key ${key} => ${result}`);

        return result;
    }

    key(index: number): string | null {
        const result = Array.from(this.#data.keys())[index] ?? null;
        logger.debug(`${this.#instanceId}: key N ${index} => ${result}`);

        return result;
    }

    removeItem(key: string): void {
        logger.debug(`${this.#instanceId}: ${new Error(`removeItem "${key}"`).stack}`);

        this.#data.delete(key);
    }

    setItem(key: string, value: string): void {
        const prevValue = this.#data.get(key) ?? null;
        logger.debug(
            `${this.#instanceId}: ` + value === ''
                ? new Error(`set ${key} to empty (prev: ${prevValue})`).stack
                : `set ${key} => ${value} (prev: ${prevValue})`
        );

        const maybeParsedValue = specialKeys.includes(key) ? safeParse(value) : null;
        const maybeParsedPrevValue = specialKeys.includes(key) ? safeParse(prevValue) : null;

        // forbids changing valid discovery data to invalid
        const skipSettingValue =
            (key === DISCOVERY_INITIAL &&
                !isValidInitialDiscovery(maybeParsedValue) &&
                isValidInitialDiscovery(maybeParsedPrevValue)) ||
            (key === DISCOVERY_EXTERNAL &&
                !isValidExternalDiscovery(maybeParsedValue) &&
                isValidExternalDiscovery(maybeParsedPrevValue));

        if (!skipSettingValue) {
            this.#data.set(key, value);
        }

        if (!specialKeys.includes(key)) return;

        // synchronizes meaningful data to the localStorage to allow sign in
        const shouldSyncToLocalStorage =
            (key === PLATFORM &&
                isValidToken(maybeParsedValue) &&
                !isGuestToken(maybeParsedValue) &&
                !isExpiredToken(maybeParsedValue)) ||
            (key === DISCOVERY_INITIAL && isValidInitialDiscovery(maybeParsedValue)) ||
            (key === DISCOVERY_EXTERNAL && isValidExternalDiscovery(maybeParsedValue));
        if (shouldSyncToLocalStorage) {
            localStorage.setItem(key, value);
        }
    }
}
